<template>
  <b-row class="flex-grow-1">
    <b-col lg="auto" cols="12">
      <asset-preview :name="name"
                     :type="asset.asset_type"
                     :preview-url="previewUrl"
                     :thumb-url="asset.thumb_url"
                     :loading="asset.processing_status === 'asset_processing'"
                     :avatar-open="editing">
        <slot name="assetPreviewSlot"></slot>
      </asset-preview>
    </b-col>
    <b-col lg="" cols="12">
      <b-row>
        <b-col>
          <b-row>
            <b-col cols="6">
              <b-form-group label="NAME" label-class="text-truncate">
                <b-input type="text" v-model="name" :disabled="!editing"/>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group label="REFERENCE NUMBER" label-class="text-truncate">
                <b-input type="text" disabled :value="asset.reference_number"/>
              </b-form-group>
            </b-col>
          </b-row>
          <div v-show="editing" class="pb-2">
            <b-row>
              <b-col xl="6" lg="12">
                <tag-select field-name="CATEGORIES"
                            :tags="categories"
                            :multiple="true"
                            :select-picker-options="{maxOptions: 3}"
                            v-model="selectedCategories"/>
              </b-col>

              <b-col xl="6" lg="12"
                     v-for="(tagCategoryData, tagCategoryName) in tagStore.tagsByCategory">
                <tag-select :field-name="tagCategoryName"
                            :tags="tagCategoryData.tags"
                            :multiple="true"
                            :select-picker-options="{maxOptions: tagCategoryData.max_options}"
                            v-model="selectedTags[tagCategoryName]"/>
              </b-col>

              <b-col lg="12">
                <b-form-group label="KEYWORDS (Separate Tags by comma, or hitting ENTER)" label-class="text-truncate">
                  <select2 v-model="keywords"
                           :options="keywordOptions"
                           :settings="{ tags: true, multiple: true, placeholder: 'Type the tags', tokenSeparators: [',', ';'] }"/>
                </b-form-group>
              </b-col>
            </b-row>

            <asset-metadata :metadata="assetMetadata"/>

            <b-form-row>
              <b-col>
                <div class="d-flex mt-2">
                  <label class="mr-2 flex-grow-1" style="flex-basis: 0">
                    Will this content only be licensed for sale by pocstock?
                  </label>
                  <b-form-radio-group v-model="exclusive"
                                      value-field="item"
                                      text-field="name"
                                      :options="exclusiveOptions"/>
                </div>

              </b-col>
            </b-form-row>
          </div>

          <release-form-list ref="releaseFormList"
                             :asset="asset"
                             :assets-url="url"
                             :initial-release-forms="releaseForms"
                             @releaseRequiredEditableChanged="releaseRequiredEditable = arguments[0]"/>

        </b-col>

        <b-col cols="12" lg="auto">
          <card-options
              :editing="editing"
              :saving="saving"
              @editing="editing = !editing"
              @saveAsset="handleAssetSave">
            <template v-slot:dropdown-options>
              <b-dropdown-item-button v-if="asset.usage === 'commercial'"
                                      @click="handleAddRelease">
                Add Release Form
              </b-dropdown-item-button>

              <b-dropdown-item-button
                  @click="changeReleaseRequired"
                  v-if="releaseRequiredEditable">
                No Release Needed
              </b-dropdown-item-button>

              <b-dropdown-item-button
                  @click="handleAssetRemove">
                Delete Asset
              </b-dropdown-item-button>
            </template>
          </card-options>
        </b-col>
      </b-row>
      <div class="text-danger" v-if="processingErrorMessage">{{processingErrorMessage}}</div>
    </b-col>
  </b-row>
</template>

<script>
import select2 from 'v-select2-component'
import AssetPreview from "../../Asset/AssetPreview"
import TagSelect from '../../Common/TagSelect'
import CardOptions from '../../Asset/CardOptions'
import ReleaseFormList from '../../ReleaseForms/ReleaseFormList'
import AssetMixins from '../../../mixins/AssetMixins'
import generalMixins from '../../../mixins/GeneralMixins'
import AssetMetadata from "../../Asset/AssetMetadata"

export default {
  props: [
    'asset',
    'categories',
    'invalidAssetIds',
    'tagStore',
    'url',
    'releaseFormUrl',
    'releaseForms'
  ],

  data: function () {
    return {
      editing: false,
      saving: false,
      name: this.asset.name || "",
      showAssetPreview: false,
      exclusive: this.asset.exclusive,
      selectedCategories: this.asset.category_ids || [],
      selectedTags: this.getSelectedTags(this.asset, this.tagStore),
      keywords: this.asset.keywords || [],
      keywordOptions: this.asset.keywords || [],
      releaseRequiredEditable: false,
      invalid: false,
      assetMetadata: this.asset.metadata
    }
  },

  methods: {
    handleAddRelease: function () {
      this.$emit('addRelease');
    },

    changeReleaseRequired: function () {
      this.$refs.releaseFormList.handleReleaseRequiredToggle();
    },

    // Update the vue component's model data with new asset data
    updateWithAsset: function (asset) {
      this.name = asset.name;
      this.exclusive = asset.exclusive;
      this.selectedCategories = asset.category_ids || [];
      this.selectedTags = this.getSelectedTags(asset, this.tagStore);
      this.keywords = asset.keywords || [];
      this.assetMetadata = asset.metadata;
    },

    handleAssetRemove: function () {
      if (confirm("Are you sure you want to delete this asset?")) {
        this.$emit('remove', this.asset)
      }
    },

    handleAssetSave: function () {
      this.saveAsset(this.assetFormData, true)
    },
  },

  computed: {
    previewUrl() {
      if (this.asset.asset_type === "Illustration") {
        return this.asset.media_thumb_url
      } else if (this.asset.asset_type === "Video") {
        return this.asset.media_thumb_url || this.asset.asset_url
      } else {
        return this.asset.asset_url
      }
    },

    hasProcessingError() {
      return this.asset.processing_status === "asset_processing_failed"
    },

    processingErrorMessage() {
      if (this.hasProcessingError && this.asset.processing_failure_message) {
        return this.asset.processing_failure_message
      }
    },

    exclusiveOptions() {
      return [{item: true, name: 'YES'}, {item: false, name: 'NO'}]
    },

    assetFormData: function () {
      return {
        name: this.name,
        category_ids: this.formDataArray(this.selectedCategories),
        keywords: this.formDataArray(this.keywords),
        tag_ids: this.formDataArray(this.formSelectedTags),
        exclusive: this.exclusive
      }
    }
  },

  mixins: [AssetMixins, generalMixins],

  components: {select2, AssetPreview, TagSelect, CardOptions, ReleaseFormList, AssetMetadata}
}
</script>